// Presentational atoms shared by the process map's side panels and the tab band above them:
// the panel's typographic scale and chrome, plus the few marks (a step's app logo, an object
// badge) that every panel renders the same way.
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  AppWindow,
  ArrowRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  MessageCircleQuestion,
  Search,
  X,
} from "lucide-react";
import React from "react";
import { useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { appIconUri } from "./appIcon";

/** Keydown targets that own the arrow keys themselves, so panel-level shortcuts stand aside. */
const TYPING_TARGETS =
  "input, textarea, select, [contenteditable='true'], [role='menu'], [role='listbox'], [role='dialog'], [role='separator']";

/** Marks a control that is one of a wheel's own rows, so Enter is the panel's rather than its. */
const WHEEL_ROW_TARGET = "data-wheel-row";

/** Whether an arrow key landed somewhere that handles it itself, rather than leaving it to the panel. */
export const ownsArrowKeys = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(TYPING_TARGETS) !== null;

/**
 * Whether Enter landed on something that already acts on it — a field, an open menu, or any
 * control other than a wheel row. Row buttons are exempt: clicking a row leaves the focus on it,
 * and a key that only re-picks the row the wheel already rests on is a key that does nothing.
 */
export const ownsEnterKey = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      `${TYPING_TARGETS}, a[href], [role='button'], button:not([${WHEEL_ROW_TARGET}])`,
    ),
  );
};

/**
 * How the panel should scroll: instantly for a reader who has asked for less motion, smoothly
 * otherwise. Read per call rather than cached, so flipping the OS setting takes effect at once.
 */
export const panelScrollBehavior = (): ScrollBehavior =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";

/**
 * Keeps a disclosure's body in view when it opens: a section expanded near the panel's bottom edge
 * would otherwise unfold below the fold with nothing pulling the scroller after it. Takes the ref
 * of the element that grows — the whole disclosure, so its tail is what gets revealed.
 * Only an open that follows a closed render scrolls, so mounting already-open never moves the panel.
 */
const useRevealOnExpand = <T extends HTMLElement>(
  expanded: boolean,
  ref: React.RefObject<T | null>,
) => {
  const wasExpandedRef = useRef(expanded);

  useLayoutEffect(() => {
    const justOpened = expanded && !wasExpandedRef.current;
    wasExpandedRef.current = expanded;
    if (!justOpened) return;
    const element = ref.current;
    if (!element) return;
    // Charts and lists inside settle on the frame after paint; measuring before then under-scrolls.
    const frame = window.requestAnimationFrame(() =>
      // "nearest" scrolls the least it can: it lifts a cut-off body's tail into view, and falls back
      // to aligning the header on top when the expanded body is taller than the panel.
      element.scrollIntoView?.({ behavior: panelScrollBehavior(), block: "nearest" }),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [expanded, ref]);
};

/**
 * Open/closed state for sections that opt into stickiness via `persistKey`, so that expanding e.g.
 * App usage on one step keeps it expanded while the user browses other steps.
 */
const stickySectionOpenState = new Map<string, boolean>();

export const CollapsibleSidebarSection = ({
  title,
  children,
  className = "",
  defaultOpen = true,
  persistKey,
  headerActions = null,
  titleAccessory = null,
  headerIcon,
  summary,
  separated = true,
  open: controlledOpen,
  onOpenChange,
  dataTour,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
  defaultOpen?: boolean;
  /** When set, the section remembers its open state across remounts and step changes. */
  persistKey?: string;
  headerActions?: React.ReactNode;
  titleAccessory?: React.ReactNode;
  headerIcon?: React.ReactNode;
  summary?: string;
  separated?: boolean;
  /** Set to drive the section from outside; the caller then owns the state via `onOpenChange`. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** data-tour anchor, for sections the guided demos point at and expand. */
  dataTour?: string;
}) => {
  const { t } = useTranslation();
  const contentId = useId();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(
    () => (persistKey ? stickySectionOpenState.get(persistKey) : undefined) ?? defaultOpen,
  );
  const open = controlledOpen ?? uncontrolledOpen;
  const toggle = () => {
    const next = !open;
    if (persistKey) stickySectionOpenState.set(persistKey, next);
    setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const sectionRef = useRef<HTMLElement>(null);
  useRevealOnExpand(open, sectionRef);
  const toggleLabel = open
    ? t("collapse_section", { section: title })
    : t("expand_section", { section: title });

  return (
    <section
      ref={sectionRef}
      data-tour={dataTour}
      className={`${separated ? "mt-3 border-t-2 border-outline-strong pt-3" : ""} ${className}`}
    >
      <div className="mb-1.5 flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-0.5">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={contentId}
            aria-label={toggleLabel}
            title={toggleLabel}
            onClick={toggle}
            className={
              headerIcon
                ? "group flex min-w-0 flex-1 items-center gap-3 rounded-md py-1 text-left text-text transition hover:text-info focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                : "group -ml-1 inline-flex min-w-0 items-center gap-1 rounded-sm py-0.5 pr-1 text-left text-[11px] font-semibold uppercase tracking-wide text-text-dim transition hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            }
          >
            {headerIcon && (
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-info/10 text-info-strong">
                {headerIcon}
              </span>
            )}
            {headerIcon && (
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">{title}</span>
                {summary && (
                  <span className="mt-0.5 block text-xs font-normal leading-relaxed text-text-dim">
                    {summary}
                  </span>
                )}
              </span>
            )}
            <ChevronRight
              className={`h-3 w-3 shrink-0 transition-transform duration-150 ${
                open ? "rotate-90" : ""
              }`}
              aria-hidden="true"
            />
            {!headerIcon && <span className="truncate">{title}</span>}
          </button>
          {titleAccessory && (
            // Same font size as the title button, so `items-center` lines the two line boxes up
            // exactly rather than centring a taller inherited one against it.
            <span className="shrink-0 text-[11px] font-medium tabular-nums text-text-mid">
              {titleAccessory}
            </span>
          )}
        </div>
        {headerActions}
      </div>
      {open && (
        <div id={contentId} className={headerIcon ? "mt-3" : undefined}>
          {children}
        </div>
      )}
    </section>
  );
};

export const ChartTooltip = ({
  x,
  y,
  width,
  height,
  label,
  wrap = false,
  maxWidth = 320,
}: {
  x: number;
  y: number;
  width: number;
  /** When set, x/y are SVG view-box coordinates and are converted to responsive percentages. */
  height?: number;
  label: React.ReactNode;
  /** Keep longer chart explanations inside the plot instead of overflowing its edges. */
  wrap?: boolean;
  maxWidth?: number;
}) => {
  const tooltipWidth = Math.min(maxWidth, width);
  const align = x < 48 ? "0%" : x > width - 48 ? "-100%" : "-50%";
  const ty = "calc(-100% - 8px)";
  return (
    <div
      role="tooltip"
      className={`pointer-events-none absolute z-30 rounded-md border border-outline bg-surface-strong px-1.5 py-0.5 text-[11px] tabular-nums text-text shadow-sm ${wrap ? "whitespace-normal" : "whitespace-nowrap"}`}
      style={{
        left: wrap
          ? Math.min(width - tooltipWidth / 2, Math.max(tooltipWidth / 2, x))
          : height
            ? `${(x / width) * 100}%`
            : x,
        top: height ? `${(y / height) * 100}%` : y,
        width: wrap ? tooltipWidth : undefined,
        transform: `translate(${wrap ? "-50%" : align}, ${ty})`,
      }}
    >
      {label}
    </div>
  );
};

/**
 * The "?" beside a reading whose definition is not self-evident. Every panel had grown its own
 * copy of this button and its tooltip; they are one control, so they are one component.
 *
 * Callers render it inside a `Tooltip.Provider` where they already have one (a metric table, a
 * strip of them); `standalone` supplies a provider for the lone "?" that has none.
 */
export const HelpHint = ({
  label,
  text,
  side = "top",
  align,
  standalone = false,
  contentClassName = "z-[60]",
  children,
}: {
  /** Accessible name for the trigger — what the reading is, not what the tooltip says. */
  label: string;
  text: string;
  side?: "top" | "bottom";
  align?: "start" | "center" | "end";
  standalone?: boolean;
  /**
   * The layer the tooltip lands on. It portals to the body, so a "?" inside an
   * overlay has to name a z-index above that overlay's own to be seen at all.
   */
  contentClassName?: string;
  /**
   * Visible text that shares the trigger with the "?", such as a column header, so pointing at
   * the words opens the hint too. It keeps the surrounding text style.
   */
  children?: React.ReactNode;
}) => {
  const hint = (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          className={`inline-flex shrink-0 items-center transition hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${children ? "gap-1 rounded [letter-spacing:inherit] [text-transform:inherit]" : "h-4 w-4 justify-center rounded-full text-text-dim"}`}
        >
          {children}
          <CircleHelp className="h-3 w-3 shrink-0" aria-hidden="true" />
        </button>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side={side}
          align={align}
          sideOffset={6}
          className={`pd-theme max-w-64 rounded-md border border-outline bg-surface px-2.5 py-2 text-xs font-normal normal-case leading-snug tracking-normal text-text shadow-lg ${contentClassName}`}
        >
          {text}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
  return standalone ? <Tooltip.Provider delayDuration={200}>{hint}</Tooltip.Provider> : hint;
};

/**
 * Opens a list cut to its first rows, and folds it back: a full-width link under the rows, which
 * names what showing all of them adds up to.
 */
export const ShowAllButton = ({
  expanded,
  label,
  onClick,
}: {
  expanded: boolean;
  /** What the folded list offers, e.g. "Show all 12 cases". */
  label: string;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-expanded={expanded}
      onClick={onClick}
      className="mt-2 w-full rounded-md py-2 text-xs font-medium text-info hover:bg-surface-strong focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      {expanded ? t("show_less") : label}
    </button>
  );
};

/**
 * A row's question, offered on hover rather than at rest: a list of findings should read as
 * findings, not as a column of buttons. The row is the hover group, so it carries `group`.
 */
export const AskWhyButton = ({
  label,
  onClick,
  className = "",
}: {
  /** Accessible name naming the specific question — the visible text is always "Ask why". */
  label: string;
  onClick: () => void;
  /** Placement within the row; the appearance and the hover reveal are fixed. */
  className?: string;
}) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={`${label} · ${t("chat_opens_sidebar")}`}
      className={`pointer-events-none absolute inline-flex h-7 items-center gap-1 rounded-md bg-surface-strong px-2 font-medium text-text opacity-0 shadow-sm transition group-hover:pointer-events-auto group-hover:opacity-100 focus:pointer-events-auto focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${className}`}
    >
      {t("ask_why")}
      <MessageCircleQuestion className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
};

/**
 * Whether a clamped block is actually cut off, so a "show more" appears only where there is more
 * to show. Re-measured on resize, since the panel is resizable and a narrower column wraps.
 */
export const useOverflowClamp = <T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  expanded: boolean,
  ...deps: unknown[]
): boolean => {
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    if (expanded) return;
    const element = ref.current;
    if (!element) return;
    const measure = () => setOverflows(element.scrollHeight > element.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
    // biome-ignore lint/correctness/useExhaustiveDependencies: the caller names what re-measures.
  }, [expanded, ref, ...deps]);
  return overflows;
};

/**
 * A step's app logo, the same mark the map's boxes carry. Where no logo is known the app is named
 * in text instead — except in a grid, where `fallback="space"` holds the column open so the names
 * beside it still line up, and beside the app's name, where `fallback="none"` leaves the name alone
 * and `fallback="generic"` keeps a box from standing empty.
 */
export const StepAppIcon = ({
  app,
  className = "h-4 w-4",
  fallback = "name",
}: {
  app: string | null | undefined;
  className?: string;
  fallback?: "name" | "space" | "none" | "generic";
}) => {
  const iconUri = appIconUri(app ?? null);
  if (iconUri) {
    return <img src={iconUri} alt="" className={`${className} shrink-0 object-contain`} />;
  }
  if (fallback === "none") return null;
  if (fallback === "generic") {
    return (
      <AppWindow
        aria-hidden="true"
        strokeWidth={1.5}
        className={`${className} shrink-0 text-text-dim`}
      />
    );
  }
  if (fallback === "space") return <span aria-hidden="true" className={`${className} shrink-0`} />;
  return app ? <span className="shrink-0 text-[10px] text-text-dim">({app})</span> : null;
};

/**
 * A section on a tab's landing page, separated by spacing and a rule rather than an outer card.
 * Tables and charts keep their own visual boundaries without another box around them.
 * Headings wrap when the sidebar narrows; actions move below them instead of squeezing the title.
 */
export const PanelSection = ({
  title,
  help,
  description,
  action,
  children,
  className = "mt-6 border-t border-outline pt-5",
}: {
  title: string;
  /** A compact definition beside the heading. */
  help?: string;
  /** What the section reads, in a line under the title: on screen, not behind a "?". */
  description?: string;
  /** Moves the reader to where this section's full reading lives. */
  action?: { label: string; onClick: () => void };
  children: React.ReactNode;
  className?: string;
}) => {
  const titleId = useId();
  return (
    <section aria-labelledby={titleId} className={`min-w-0 ${className}`}>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-[1_1_12rem]">
          <div className="flex items-center gap-1.5">
            <h3
              id={titleId}
              className="text-lg font-semibold leading-snug tracking-tight text-text"
            >
              {title}
            </h3>
            {help && <HelpHint standalone label={title} text={help} />}
          </div>
          {description && (
            <p className="mt-1 text-xs leading-relaxed text-text-mid">{description}</p>
          )}
        </div>
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="inline-flex min-h-6 shrink-0 items-center gap-1 rounded-md text-sm font-medium text-info transition hover:text-info-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
          >
            {action.label}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
      </div>
      {children}
    </section>
  );
};

type Kpi = {
  label: string;
  value: React.ReactNode;
  /** A "?" beside the label, for a reading whose definition is not self-evident. */
  help?: string;
  title?: string;
};

/**
 * The few headline numbers at the top of a tab, each in a box of its own so they read as the
 * tab's answer before its detail. Four across where the panel is wide, two where it is not.
 */
export const KpiTiles = ({ items }: { items: Kpi[] }) => (
  <div className="@container">
    <dl className="grid grid-cols-2 gap-2 @lg:grid-cols-4">
      {items.map(item => (
        // Value first on screen, label first for assistive tech: dt must precede its dd.
        <div
          key={item.label}
          className="flex min-w-0 flex-col-reverse justify-end gap-1 rounded-lg border border-outline bg-surface px-3 py-2.5"
        >
          <dt title={item.title} className="flex min-w-0 items-center gap-1 text-xs text-text-dim">
            <span className="truncate">{item.label}</span>
            {item.help && <HelpHint standalone label={item.label} text={item.help} />}
          </dt>
          <dd className="truncate text-xl font-semibold leading-tight tabular-nums text-text">
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  </div>
);

/**
 * A value and its share, side by side in fixed columns so a list's numbers line up without a
 * divider between them: the share sits in a muted column of its own. Both columns hold their
 * width, so a bar beside the pair starts and ends at the same place on every row.
 */
export const ReadingPair = ({
  value,
  share,
  className = "",
}: {
  value: React.ReactNode;
  share: React.ReactNode;
  className?: string;
}) => (
  <span
    className={`flex shrink-0 items-baseline gap-3 whitespace-nowrap tabular-nums ${className}`}
  >
    <span className="min-w-12 text-right text-text-mid">{value}</span>
    <span className="w-10 text-right text-text-dim">{share}</span>
  </span>
);

/**
 * Names the two columns of a list of ReadingPairs, right above them. Both sides are right-aligned
 * on the same share column, so the labels sit over their numbers whatever their length.
 */
export const ReadingHeader = ({
  value,
  share,
  className = "",
}: {
  value: string;
  share: string;
  className?: string;
}) => (
  <div
    aria-hidden="true"
    className={`flex justify-end gap-3 whitespace-nowrap text-[10px] font-medium uppercase tracking-wide text-text-dim ${className}`}
  >
    <span>{value}</span>
    <span className="w-10 text-right">{share}</span>
  </div>
);

/** Rows per page in the panel's tables: a screenful at the default panel height. */
export const TABLE_PAGE_SIZE = 10;

/** "11–20 of 94" with previous/next, for the panel's paged tables. */
export const TablePager = ({
  page,
  total,
  onPageChange,
}: {
  /** Zero-based. */
  page: number;
  total: number;
  onPageChange: (page: number) => void;
}) => {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / TABLE_PAGE_SIZE));
  if (total <= TABLE_PAGE_SIZE) return null;
  const buttonClass =
    "inline-flex h-8 w-8 items-center justify-center rounded-md border border-outline bg-surface text-text-mid transition hover:bg-surface-strong hover:text-text disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30";
  return (
    <nav
      className="mt-2 flex items-center justify-end gap-2"
      aria-label={t("processPanel.pagination")}
    >
      <span aria-live="polite" className="text-sm tabular-nums text-text-dim">
        {t("processPanel.pageRange", {
          from: page * TABLE_PAGE_SIZE + 1,
          to: Math.min(total, (page + 1) * TABLE_PAGE_SIZE),
          total,
        })}
      </span>
      <button
        type="button"
        aria-label={t("processPanel.previousPage")}
        title={t("processPanel.previousPage")}
        disabled={page === 0}
        onClick={() => onPageChange(page - 1)}
        className={buttonClass}
      >
        <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label={t("processPanel.nextPage")}
        title={t("processPanel.nextPage")}
        disabled={page >= pages - 1}
        onClick={() => onPageChange(page + 1)}
        className={buttonClass}
      >
        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </nav>
  );
};

/** A column header that sorts its table: clicking it again flips the direction. */
export const SortHeader = <K extends string>({
  label,
  title,
  column,
  sort,
  onSort,
  align = "left",
}: {
  label: string;
  /** What the column counts, where the label alone can't say. */
  title?: string;
  column: K;
  sort: { column: K; descending: boolean };
  onSort: (column: K) => void;
  align?: "left" | "right";
}) => {
  const { t } = useTranslation();
  const active = sort.column === column;
  return (
    <span className={`flex items-center gap-0.5 ${align === "right" ? "justify-end" : ""}`}>
      <button
        type="button"
        onClick={() => onSort(column)}
        aria-label={t("processPanel.sortBy", { column: label })}
        title={title}
        aria-sort={active ? (sort.descending ? "descending" : "ascending") : undefined}
        className={`inline-flex items-center gap-0.5 rounded-sm uppercase tracking-wide transition hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${
          active ? "text-text" : ""
        }`}
      >
        {label}
        <ChevronDown
          aria-hidden="true"
          className={`h-3 w-3 transition-transform ${active ? "" : "opacity-0"} ${
            active && !sort.descending ? "rotate-180" : ""
          }`}
        />
      </button>
    </span>
  );
};

/** The search box above a panel table; `label` doubles as the placeholder. */
export const PanelSearchInput = ({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
}) => (
  <label className="relative flex min-w-40 flex-1 items-center">
    <Search
      className="pointer-events-none absolute left-2.5 h-4 w-4 text-text-dim"
      aria-hidden="true"
    />
    <input
      type="search"
      value={value}
      onChange={event => onChange(event.target.value)}
      placeholder={label}
      aria-label={label}
      className="h-9 w-full rounded-md border border-outline bg-surface pl-8 pr-2 text-sm text-text placeholder:text-text-dim focus:border-outline-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
    />
  </label>
);

/** Sizes an AppSelect to sit in a panel toolbar beside PanelSearchInput. */
export const PANEL_SELECT_CLASS =
  "min-w-36 [&_.dropdown-trigger]:h-9 [&_.dropdown-trigger]:px-2.5 [&_.dropdown-trigger]:text-sm";

/** An active narrowing of the table, named, with the way to drop it. */
export const FilterChip = ({ label, onClear }: { label: string; onClear: () => void }) => {
  const { t } = useTranslation();
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-focus-band py-1 pl-3 pr-1 text-sm font-medium text-focus-band-ink">
      <span className="truncate">{label}</span>
      <button
        type="button"
        onClick={onClear}
        aria-label={`${t("processPanel.clearFilter")}: ${label}`}
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition hover:bg-focus-band-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
    </span>
  );
};
